import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering, CatalogRunningNumberKeys, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryLot, InventorySerial
} from './catalog.service';

type LotTab = 'general' | 'serials';

@Component({
  standalone: true,
  selector: 'ain-inventory-lot-detail',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.tracking.lots.createTitle') : t('catalog.tracking.lots.detailTitle')" [description]="t('catalog.tracking.lots.detailDescription')">
      <ain-inventory-page-hint pageKey="lotDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !lot()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.tracking.lots.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.tracking.lots.newRecord') : lot()!.lotNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.tracking.lots.createHint') : lot()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/inventory/lots']">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.tracking.action.quarantine')" severity="warn" (click)="updateStatus('Quarantine')"></button>
              <button pButton type="button" [label]="t('catalog.tracking.action.release')" severity="secondary" (click)="updateStatus('Release')"></button>
            }
            <button pButton type="button" [label]="t('catalog.action.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          @if (!isCreateMode()) {
            <button type="button" [class.active]="activeTab() === 'serials'" (click)="activeTab.set('serials')">{{ t('catalog.tracking.serials.tab') }}</button>
          }
        </section>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.tracking.lotNumber') }}</span>
              <input
                pInputText
                type="text"
                [ngModel]="form.lotNumber"
                (ngModelChange)="onLotNumberChange($event)"
                [disabled]="!isCreateMode() || isAutoLotNumber"
                [placeholder]="t('catalog.tracking.numberAuto')" />
              @if (isCreateMode() && isAutoLotNumber) {
                <button type="button" class="manual-link" (click)="enableManualLotNumber()">{{ t('catalog.tracking.enterManually') }}</button>
              }
            </label>
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [options]="offeringOptions()" [ngModel]="form.offeringId" (ngModelChange)="onOfferingChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.warehouseId') }}</span>
              <p-select [options]="warehouseOptions()" [ngModel]="form.warehouseId" (ngModelChange)="onWarehouseChange($event)" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.binId') }}</span>
              <p-select [options]="binOptions()" [(ngModel)]="form.binId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.quantity') }}</span>
              <p-inputNumber [(ngModel)]="form.quantity" [min]="0" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.manufacturedDate') }}</span>
              <ain-date-input [(ngModel)]="form.manufacturedDate" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.expiryDate') }}</span>
              <ain-date-input [(ngModel)]="form.expiryDate" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.sourceReference') }}</span>
              <input pInputText type="text" [(ngModel)]="form.sourceReferenceNumber" [disabled]="!isCreateMode()" />
            </label>
          </section>
        }

        @if (activeTab() === 'serials' && !isCreateMode()) {
          <p-table [value]="serials()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.tracking.serialNumber') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><a [routerLink]="['/inventory/serials', row.id]">{{ row.serialNumber }}</a></td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
    .tabs button { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tabs button.active { border-color: #3b82f6; color: #1d4ed8; font-weight: 600; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .manual-link { justify-self: start; border: none; background: none; padding: 0; color: #2563eb; cursor: pointer; font-size: 12px; text-decoration: underline; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryLotDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly lot = signal<InventoryLot | null>(null);
  readonly serials = signal<InventorySerial[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly activeTab = signal<LotTab>('general');

  isAutoLotNumber = true;

  readonly form = {
    lotNumber: '',
    offeringId: '',
    warehouseId: '',
    binId: '',
    quantity: 1,
    manufacturedDate: null as string | null,
    expiryDate: null as string | null,
    sourceReferenceNumber: ''
  };

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly offeringOptions = computed(() => this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id })));
  readonly warehouseOptions = computed(() => this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id })));
  readonly binOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.bins().map(b => ({ label: `${b.code} - ${b.name}`, value: b.id }))
  ]);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows.filter(o => o.inventoryEnabled)) });
    this.service.listWarehouses().subscribe({ next: rows => this.warehouses.set(rows) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    if (this.isCreateMode()) {
      this.isAutoLotNumber = true;
      this.loadLotNumberPreview();
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.service.getInventoryLot(id).subscribe({
      next: lot => {
        this.lot.set(lot);
        this.form.lotNumber = lot.lotNumber;
        this.form.offeringId = lot.offeringId;
        this.form.warehouseId = lot.warehouseId;
        this.form.binId = lot.binId ?? '';
        this.form.quantity = lot.quantity;
        this.form.manufacturedDate = lot.manufacturedDate;
        this.form.expiryDate = lot.expiryDate;
        this.form.sourceReferenceNumber = lot.sourceReferenceNumber;
        this.loadBins(lot.warehouseId);
        this.service.listInventorySerials(undefined, lot.id).subscribe({ next: serials => this.serials.set(serials) });
        this.loading.set(false);
      },
      error: () => {
        this.lot.set(null);
        this.error.set(this.t('catalog.tracking.lots.notFound'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    if (!this.isCreateMode()) return;

    if ((!this.isAutoLotNumber && !this.form.lotNumber.trim()) || !this.form.offeringId || !this.form.warehouseId) {
      this.error.set(this.t('catalog.tracking.lots.requiredFields'));
      return;
    }

    this.service.createInventoryLot({
      offeringId: this.form.offeringId,
      warehouseId: this.form.warehouseId,
      binId: this.form.binId || null,
      lotNumber: this.isAutoLotNumber ? '' : this.form.lotNumber.trim(),
      quantity: this.form.quantity,
      manufacturedDate: this.toDateOnly(this.form.manufacturedDate),
      expiryDate: this.toDateOnly(this.form.expiryDate),
      sourceReferenceNumber: this.form.sourceReferenceNumber.trim()
    }).subscribe({
      next: created => void this.router.navigate(['/inventory/lots', created.id]),
      error: () => this.error.set(this.t('catalog.tracking.lots.createFailed'))
    });
  }

  updateStatus(status: string): void {
    const lot = this.lot();
    if (!lot) return;

    this.service.updateInventoryLotStatus(lot.id, status).subscribe({
      next: updated => this.lot.set(updated),
      error: () => this.error.set(this.t('catalog.tracking.lots.updateFailed'))
    });
  }

  onWarehouseChange(warehouseId: string): void {
    this.form.warehouseId = warehouseId;
    this.form.binId = '';
    this.loadBins(warehouseId);
  }

  onOfferingChange(offeringId: string): void {
    this.form.offeringId = offeringId;
    this.loadLotNumberPreview();
  }

  onLotNumberChange(value: string): void {
    this.form.lotNumber = value;
    this.isAutoLotNumber = false;
  }

  enableManualLotNumber(): void {
    this.isAutoLotNumber = false;
    this.form.lotNumber = '';
  }

  private loadLotNumberPreview(): void {
    if (!this.isCreateMode() || !this.isAutoLotNumber) return;

    this.service.previewNextRunningNumber(
      CatalogRunningNumberKeys.inventoryLot,
      CatalogRunningNumberKeys.offeringParent,
      this.form.offeringId || undefined
    ).subscribe({
      next: preview => {
        if (preview && this.isAutoLotNumber) {
          this.form.lotNumber = preview;
        }
      }
    });
  }

  private loadBins(warehouseId: string): void {
    if (!warehouseId) {
      this.bins.set([]);
      return;
    }

    this.service.listWarehouseBins(warehouseId).subscribe({ next: rows => this.bins.set(rows) });
  }

  private toDateOnly(value: string | null): string | null {
    if (!value) return null;
    return value.slice(0, 10);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
